import type { FC } from 'react';
import type { PageProps } from 'gatsby';
import type { Theme } from '@mui/material';
import React from 'react';
import ReactMarkdown from 'react-markdown';
import { Helmet } from 'react-helmet';
import {
  Box,
  Container,
  Typography,
  useMediaQuery
} from '@mui/material';
import { ParallaxBanner, ParallaxProvider } from 'react-scroll-parallax';
import AppBar from '../components/AppBar';
import Quote from '../components/Quote';
import Link from '../components/Link';
import PodcastCard from '../components/PodcastCard';
import Program from '../components/Program';

interface ProjectPageProps extends PageProps {
  pageContext: {
    content: {
      title?: string;
      image?: string;
      quote?: string;
      description?: string;
      video?: string;
      program?: string;
      podcast?: {
        title: string;
        url: string;
        transcript?: string;
        credits?: {
          title: string;
          roles?: {
            name: string;
            role: string;
          }[];
          names?: string[];
          name?: string;
        }[];
      }[];
    }
  }
}

const Project: FC<ProjectPageProps> = (props) => {
  const { pageContext } = props;
  const { content } = pageContext;
  const descriptionMd = content.description
    .replace(content.title, `**${content.title}**`) // Highlight title
    .replace(/\n|\r/g, '\n\n'); // Fix newlines

  const mdUp = useMediaQuery((theme: Theme) => theme.breakpoints.up(theme.breakpoints.values.md));

  return (
    <ParallaxProvider>
      <Helmet title={`${content.title} | Scawwy Howwow Theatre, Inc.`} />
      <AppBar />
      {content.image && (
        <ParallaxBanner
          style={{ backgroundColor: 'white', height: '60vh' }}
          layers={[
            {
              image: `/static/${content.image}`,
              expanded: true,
              speed: -25,
            }
          ]}
        />
      )}
      <Container
        maxWidth="xl"
        sx={{ my: 6 }}
      >
        <Typography variant="h1">
          {content.title}
        </Typography>
        {content.quote && (
          <Quote>
            {content.quote}
          </Quote>
        )}
        <ReactMarkdown
          children={descriptionMd}
          components={{
            a: Link,
            p: (params) => <Typography sx={{ mb: 3 }} variant="body1" color="textSecondary" {...params} />
          }}
        />
        {content.podcast && content.podcast.map((episode) => (
          <PodcastCard
            src={episode.url}
            title={episode.title}
            credits={episode.credits}
            transcript={episode.transcript}
          />
        ))}
        {content.video && (
          <Box sx={{ position: 'relative', paddingBottom: '56.25%' }}>
            <iframe
              src={content.video}
              frameBorder="0"
              allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%'
              }}
              allowFullScreen
            >
              <Link href={content.video}>Click here to view in another window</Link>
            </iframe>
          </Box>
        )}
        {mdUp && content.program && (
          <Program src={content.program} />
        )}
      </Container>
    </ParallaxProvider>
  );
};

export default Project;
